
import React from 'react';
import { Button } from '@/components/ui/button';
import { Heart, MessageSquare, X, Check } from 'lucide-react';

interface ConnectionActionsProps {
  isMatch: boolean;
  isSentLike: boolean;
  isReceived?: boolean;
  isDeleting: boolean;
  onLike: () => void;
  onMessage: () => void;
  onUnlike: () => void;
  onViewProfile?: () => void;
  canLike?: boolean;
  likeLimits?: {
    current: number;
    limit: number;
    remaining: number;
    isUnlimited: boolean;
  };
}

export const ConnectionActions: React.FC<ConnectionActionsProps> = ({
  isMatch,
  isSentLike,
  isReceived = false,
  isDeleting,
  onLike,
  onMessage,
  onUnlike,
  onViewProfile,
  canLike = true,
  likeLimits
}) => {
  const getLimitText = () => {
    if (!likeLimits || likeLimits.isUnlimited) return null;
    return `${likeLimits.remaining} curtidas restantes`;
  };

  const getAcceptButtonText = () => {
    if (!likeLimits || likeLimits.isUnlimited) {
      return "Aceitar";
    }
    if (likeLimits.remaining <= 0) {
      return "Limite atingido";
    }
    return `Aceitar (${likeLimits.remaining} restantes)`;
  };

  const getLimitColor = () => {
    if (!likeLimits || likeLimits.isUnlimited) return '';
    if (likeLimits.remaining <= 1) return 'text-red-400';
    if (likeLimits.remaining <= 3) return 'text-yellow-400';
    return 'text-gray-400';
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between gap-1">
        {isMatch && (
          <>
            <Button 
              variant="outline"
              size="sm"
              className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5 h-7 text-xs px-2"
              onClick={onMessage}
              disabled={isDeleting}
            >
              <MessageSquare className="h-3 w-3 mr-1" />
              Mensagem
            </Button>
            <Button
              variant="outline" 
              size="sm"
              className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-7 w-7 p-0"
              onClick={onUnlike}
              disabled={isDeleting}
            >
              <X className="h-3 w-3" />
            </Button>
          </>
        )}
        
        {isReceived && !isMatch && !isSentLike && (
          <>
            <Button 
              size="sm"
              variant="brand-gradient"
              className="flex-1 disabled:opacity-50 h-7 text-xs px-2"
              onClick={onLike}
              disabled={isDeleting || !canLike}
            >
              <Heart className="h-3 w-3 mr-1" />
              Curtir de volta
            </Button>
            <Button
              variant="outline" 
              size="sm"
              className="bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-7 w-7 p-0"
              onClick={onUnlike}
              disabled={isDeleting}
            >
              <X className="h-3 w-3" />
            </Button>
          </>
        )}
        
        {isSentLike && !isMatch && (
          <Button
            variant="outline" 
            size="sm"
            className="flex-1 bg-transparent border-red-500/30 text-red-400 hover:bg-red-500/10 hover:text-red-300 h-7 text-xs px-2"
            onClick={onUnlike}
            disabled={isDeleting}
          >
            <X className="h-3 w-3 mr-1" />
            {isDeleting ? 'Removendo...' : 'Cancelar curtida'}
          </Button>
        )}

        {!isMatch && !isSentLike && !isReceived && (
          <Button
            variant="outline"
            size="sm"
            className="flex-1 bg-transparent border-white/20 text-white hover:bg-white/5 h-7 text-xs px-2"
            onClick={onViewProfile}
            disabled={isDeleting}
          >
            Visualizar perfil
          </Button>
        )}
      </div>
      
      {!canLike && getLimitText() && (
        <div className={`text-xs text-center transition-colors ${getLimitColor()}`}>
          {getLimitText()}
        </div>
      )}
    </div>
  );
};
